import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { CognitoUserInfo } from '@/services/aws-auth.service';
import type { UserAccountInfo } from '@/types/ringcentral';

import { awsAuth } from '@/services/aws-auth.service';
import { getCookie, setCookie } from '@/utils/cookies';
import { clearGAUserId, setGAUserId, setGAUserProperties, trackGAEvent } from '@/utils/ga4';
import { clearSentryUser, setSentryUser } from '@/utils/sentry';
import { initializeSessionAnalytics } from '@/utils/sentry-analytics';

export interface Membership {
  orgId: string;
  orgName: string;
  role: string;
  joinedAt: string;
}

/**
 * Helper function to extract Cognito sub from ID token
 * The sub is the unique user identifier in Cognito
 */
function extractCognitoSub(idToken: string): string | null {
  try {
    // JWT tokens are base64 encoded with 3 parts: header.payload.signature
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return payload.sub || null;
  } catch (error) {
    console.error('Failed to extract Cognito sub from ID token:', error);
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State with persistent storage using localStorage
  // Tokens stored in localStorage with VueUse for reactivity
  const idToken = useLocalStorage<string | null>('idToken', null);
  const accessToken = useLocalStorage<string | null>('accessToken', null);
  const refreshToken = useLocalStorage<string | null>('refreshToken', null);

  // User state
  const user = ref<CognitoUserInfo | null>(null);
  const memberships = ref<Membership[]>([]);
  const lastVisitedOrgId = useLocalStorage<string | null>('lastVisitedOrgId', null);
  const accountInfo = useLocalStorage<UserAccountInfo | null>('userAccountInfo', null, {
    serializer: {
      read: (v: string) => {
        try {
          return v === 'null' ? null : JSON.parse(v);
        } catch (error) {
          console.warn('Failed to parse userAccountInfo, clearing:', v);
          return null;
        }
      },
      write: (v: UserAccountInfo | null) => JSON.stringify(v),
    },
  });

  // Clear invalid cached values (malformed data from previous bugs)
  const validTypes = ['ROOT', 'SUB_ACCOUNT', 'GRADUATED'];
  const isInvalid =
    accountInfo.value !== null &&
    (typeof accountInfo.value !== 'object' ||
      !accountInfo.value?.accountType ||
      !validTypes.includes(accountInfo.value.accountType));

  if (isInvalid) {
    console.warn('Clearing invalid cached account info:', accountInfo.value);
    localStorage.removeItem('userAccountInfo');
    accountInfo.value = null;
  }

  // Loading state
  const loading = ref(false);
  const fetchingMemberships = ref(false);

  // Getters
  const isLogin = computed(() => !!idToken.value || !!user.value);
  // ROOT and GRADUATED accounts can create organizations
  // SUB_ACCOUNT cannot create orgs (managed by parent)
  // null = not fetched yet, allow during transition period
  const isPrimaryAccount = computed(() =>
    accountInfo.value === null ||
    accountInfo.value?.accountType === 'ROOT' ||
    accountInfo.value?.accountType === 'GRADUATED'
  );
  const canCreateOrganizations = computed(() => isPrimaryAccount.value);

  // Actions
  async function login(email: string, password: string) {
    try {
      loading.value = true;

      // Real AWS Cognito login
      const result = await awsAuth.signInUser(email, password);

      // Handle new password required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { requiresPasswordChange: true };
      }

      if (result.isSignedIn) {
        // Get user info and tokens
        const userInfo = await awsAuth.getCurrentUserInfo();
        user.value = userInfo;

        try {
          const session = await awsAuth.getSession();
          idToken.value = session.idToken;
          accessToken.value = session.accessToken;
          refreshToken.value = session.refreshToken;

          // Set GA4 user ID using Cognito sub from ID token
          if (session.idToken) {
            const cognitoSub = extractCognitoSub(session.idToken);
            if (cognitoSub) {
              setGAUserId(cognitoSub);
            }
          }

          // Set Sentry user context and initialize session analytics with cognito_sub
          if (userInfo && session.idToken) {
            const cognitoSub = extractCognitoSub(session.idToken);
            setSentryUser(cognitoSub || undefined, userInfo.email, userInfo.email);

            // Initialize Sentry session analytics with user identity on login
            if (cognitoSub) {
              initializeSessionAnalytics({
                cognitoSub,
                email: userInfo.email,
                username: userInfo.email,
                role: userInfo.role,
              });
            }
          }
        } catch (error) {
          console.debug('Could not get refresh token from session:', error);
        }
      }

      // Fetch account info to determine if user can create organizations
      try {
        await fetchUserAccountInfo(true);
      } catch (accountInfoError) {
        console.warn('Failed to fetch account info after login:', accountInfoError);
      }

      // Try to fetch memberships after login, but don't fail if it doesn't work
      // Force refresh to bypass any stale cache and ensure fresh data
      try {
        await fetchUserMemberships(true);
      } catch (membershipError) {
        console.warn(
          'Failed to fetch memberships after login, but allowing login to proceed:',
          membershipError,
        );
        // Set empty memberships array so user can still access the no-org page
        memberships.value = [];
      }

      // Initialize organization store after memberships are loaded
      try {
        const { useOrganizationStore } = await import('./organization');
        const organizationStore = useOrganizationStore();
        await organizationStore.loadUserOrganizations();
      } catch (orgError) {
        console.warn('Failed to initialize organization store after login:', orgError);
      }

      // Track login event and set user properties in GA4
      trackGAEvent('user_login', {
        org_count: memberships.value.length,
        has_organizations: memberships.value.length > 0,
      });

      // Set user properties for segmentation
      setGAUserProperties({
        org_count: memberships.value.length,
        user_role: user.value?.role || 'unknown',
        is_multi_org_user: memberships.value.length > 1,
      });

      return { requiresPasswordChange: false };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function completeNewPassword(newPassword: string) {
    try {
      loading.value = true;

      const result = await awsAuth.confirmSignInWithNewPassword(newPassword);

      if (result.isSignedIn) {
        const userInfo = await awsAuth.getCurrentUserInfo();
        user.value = userInfo;

        try {
          const session = await awsAuth.getSession();
          idToken.value = session.idToken;
          accessToken.value = session.accessToken;
          refreshToken.value = session.refreshToken;

          // Set GA4 user ID using Cognito sub from ID token
          if (session.idToken) {
            const cognitoSub = extractCognitoSub(session.idToken);
            if (cognitoSub) {
              setGAUserId(cognitoSub);
            }
          }

          // Set Sentry user context with cognito_sub for permanent user tracking
          if (userInfo && session.idToken) {
            const cognitoSub = extractCognitoSub(session.idToken);
            setSentryUser(cognitoSub || undefined, userInfo.email, userInfo.email);
          }
        } catch (error) {
          console.debug('Could not get refresh token from session:', error);
        }

        // Fetch account info
        try {
          await fetchUserAccountInfo(true);
        } catch (accountInfoError) {
          console.warn('Failed to fetch account info after password change:', accountInfoError);
        }

        try {
          await fetchUserMemberships(true);
        } catch (membershipError) {
          console.warn('Failed to fetch memberships after password change:', membershipError);
          memberships.value = [];
        }

        // Initialize organization store after memberships are loaded
        try {
          const { useOrganizationStore } = await import('./organization');
          const organizationStore = useOrganizationStore();
          await organizationStore.loadUserOrganizations();
        } catch (orgError) {
          console.warn('Failed to initialize organization store after password change:', orgError);
        }
      }

      return true;
    } catch (error) {
      console.error('Password change failed:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function loadUserFromToken(retryCount = 0) {
    // First check if we have a stored token
    const storedToken = idToken.value || getCookie('idToken') || localStorage.getItem('idToken');

    if (!storedToken) {
      console.log('No stored token found');
      return;
    }

    try {
      loading.value = true;

      // Add small delay on first attempt to allow Amplify to fully initialize
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Get user info from current session
      const userInfo = await awsAuth.getCurrentUserInfo();
      user.value = userInfo;

      // Update tokens if needed
      if (!idToken.value && storedToken) {
        idToken.value = storedToken;
        setCookie('idToken', storedToken, 7); // 7 days
      }

      // Try to get fresh tokens from session
      try {
        const session = await awsAuth.getSession();
        if (session.idToken) {
          idToken.value = session.idToken;
          accessToken.value = session.accessToken;
          refreshToken.value = session.refreshToken;

          // Set GA4 user ID when loading from token
          const cognitoSub = extractCognitoSub(session.idToken);
          if (cognitoSub) {
            setGAUserId(cognitoSub);
          }

          // Set Sentry user context and initialize session analytics with cognito_sub
          if (userInfo && cognitoSub) {
            setSentryUser(cognitoSub, userInfo.email, userInfo.email);

            // Initialize Sentry session analytics with user identity
            initializeSessionAnalytics({
              cognitoSub,
              email: userInfo.email,
              username: userInfo.email,
              role: userInfo.role,
            });
          }
        }
      } catch (error) {
        console.warn('Could not refresh session tokens:', error);
      }

      // Try to load user's account info and memberships, but don't fail if it doesn't work
      if (user.value) {
        try {
          // Force refresh if accountInfo is null (cleared due to bug or first load)
          const shouldForce = accountInfo.value === null;
          await fetchUserAccountInfo(shouldForce);
        } catch (accountInfoError) {
          console.warn('Failed to fetch account info during token load:', accountInfoError);
        }

        try {
          await fetchUserMemberships();
        } catch (membershipError) {
          console.warn(
            'Failed to fetch memberships during token load, but allowing authentication to proceed:',
            membershipError,
          );
          // Set empty memberships array so user can still access the no-org page
          memberships.value = [];
        }
      }
    } catch (error) {
      console.error('Failed to load user from token:', error);

      // Check if this is an AWS UserPool initialization error and retry once
      if (
        error instanceof Error &&
        error.message?.includes('Auth UserPool not configured') &&
        retryCount === 0
      ) {
        console.log('🔄 AWS UserPool not ready, retrying in 1 second...');
        setTimeout(() => {
          loadUserFromToken(1); // Retry once with retryCount = 1
        }, 1000);
        return;
      }

      // For other errors or if retry failed, clear invalid tokens
      if (retryCount > 0) {
        console.log('❌ Retry failed, clearing tokens');
      }
      idToken.value = null;
      accessToken.value = null;
      refreshToken.value = null;
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Convert RingCentral organizations to memberships format
   */
  function convertRingCentralOrgsToMemberships(organizations: any[]): Membership[] {
    if (!organizations || !Array.isArray(organizations)) {
      return [];
    }

    return organizations.map(org => ({
      orgId: org.organizationId,
      orgName: org.name,
      role: 'admin', // Default role for RingCentral organizations
      joinedAt: org.createdAt || new Date().toISOString(),
    }));
  }

  async function fetchUserAccountInfo(forceRefresh = false) {
    // Only fetch if user is authenticated (has id token)
    if (!idToken.value) {
      console.warn('Cannot fetch account info: user not authenticated');
      return;
    }

    // If we already have account info and not forcing refresh, skip API call
    if (!forceRefresh && accountInfo.value) {
      return;
    }

    try {
      loading.value = true;

      // Import RingCentral API
      const { ringCentralAPI } = await import('@/api/ringcentral-api');

      // Fetch account info from API
      const info = await ringCentralAPI.getUserAccountInfo();
      accountInfo.value = info;
    } catch (error) {
      // Log detailed error information
      const errorStatus = error && typeof error === 'object' && 'status' in error ? error.status : 'unknown';
      const errorMessage = error && typeof error === 'object' && 'message' in error ? error.message : String(error);
      console.error('Failed to fetch user account info:', {
        status: errorStatus,
        message: errorMessage,
        error,
      });

      // Get userId for fallback values
      const userId = idToken.value ? extractCognitoSub(idToken.value) || 'unknown' : 'unknown';

      // Check if error is 404 (endpoint not implemented yet)
      // If so, default to 'ROOT' to allow org creation until backend is ready
      // For other errors (network, auth, etc.), use fail-closed approach with 'SUB_ACCOUNT'
      const is404 = errorStatus === 404;

      if (is404) {
        console.warn('Account info API not implemented yet (404), defaulting to ROOT account');
        accountInfo.value = { accountType: 'ROOT', rootUserId: userId };
      } else {
        // For non-404 errors, default to 'SUB_ACCOUNT' to prevent accidental privilege escalation
        console.warn(`Account info API error (status: ${errorStatus}), using fail-closed approach with SUB_ACCOUNT`);
        accountInfo.value = { accountType: 'SUB_ACCOUNT', rootUserId: userId };
      }
    } finally {
      loading.value = false;
    }
  }

  async function fetchUserMemberships(forceRefresh = false) {
    // Only fetch if user is authenticated (has id token)
    if (!idToken.value) {
      return;
    }

    // If we already have memberships and not forcing refresh, skip API call
    if (!forceRefresh && memberships.value && memberships.value.length > 0) {
      return;
    }

    // Prevent concurrent calls - if already fetching, wait for that to complete
    if (fetchingMemberships.value && !forceRefresh) {
      // Wait for the ongoing fetch to complete by polling
      while (fetchingMemberships.value) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return;
    }

    try {
      fetchingMemberships.value = true;
      loading.value = true;

      // Use RingCentral API instead of legacy memberships API
      const { useRingCentralStore } = await import('@/stores/ringcentral');
      const ringCentralStore = useRingCentralStore();

      try {
        const organizations = await ringCentralStore.getUserOrganizations(forceRefresh);

        // Convert RingCentral organizations to memberships format
        memberships.value = convertRingCentralOrgsToMemberships(organizations);
      } catch (ringCentralError) {
        console.warn('⚠️ RingCentral API failed:', ringCentralError);
        // Set empty memberships array on failure to ensure consistent state
        memberships.value = [];
      }

      // Set default org if needed
      if (!lastVisitedOrgId.value && memberships.value.length > 0) {
        setLastVisitedOrgId(memberships.value[0].orgId);
      }

      // Initialize RingCentral store after memberships are loaded (if not already done)
      try {
        await ringCentralStore.initialize();
      } catch (err) {
        console.log('ℹ️ RingCentral store initialization skipped:', err);
      }
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
      memberships.value = [];
    } finally {
      fetchingMemberships.value = false;
      loading.value = false;
    }
  }

  function setTokens(tokens: { idToken: string; accessToken: string; refreshToken: string }) {
    idToken.value = tokens.idToken;
    accessToken.value = tokens.accessToken;
    refreshToken.value = tokens.refreshToken;

    // Also set cookies for persistence
    setCookie('idToken', tokens.idToken, 7);
    setCookie('accessToken', tokens.accessToken, 7);
    setCookie('refreshToken', tokens.refreshToken, 7);
  }

  function setLastVisitedOrgId(orgId: string) {
    lastVisitedOrgId.value = orgId;
  }

  async function logout() {
    try {
      // Track logout event before clearing user data
      trackGAEvent('user_logout');

      // Sign out from AWS Cognito
      await awsAuth.signOutUser();

      // Clear GA4 user ID and Sentry user context
      clearGAUserId();
      clearSentryUser();

      // Clear all stored data
      user.value = null;
      idToken.value = null;
      accessToken.value = null;
      refreshToken.value = null;
      memberships.value = [];
      accountInfo.value = null;

      // Clear cookies
      document.cookie = 'idToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // Clear localStorage
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('currentOrgId'); // Clear organization selection to prevent cross-user contamination
      localStorage.removeItem('lastVisitedOrgId');
      localStorage.removeItem('userAccountInfo');

      // Redirect to sign-in page
      window.location.href = '/auth/sign-in';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, still redirect to sign-in
      window.location.href = '/auth/sign-in';
    }
  }

  // Initialize on store creation
  const storedToken = idToken.value || getCookie('idToken') || localStorage.getItem('idToken');
  if (storedToken && storedToken !== 'null' && storedToken !== 'undefined') {
    if (!idToken.value) {
      idToken.value = storedToken;
    }
    loadUserFromToken();
  }

  return {
    user,
    idToken,
    accessToken,
    refreshToken,
    memberships,
    lastVisitedOrgId,
    accountInfo,
    loading,
    isLogin,
    isPrimaryAccount,
    canCreateOrganizations,
    login,
    completeNewPassword,
    logout,
    loadUserFromToken,
    fetchUserAccountInfo,
    fetchUserMemberships,
    setTokens,
    setLastVisitedOrgId,
  };
});
